/**
 * Which project this record is filed in, and the control that files or moves it.
 *
 * IT WAS THE ONE PLACE A RECORD COULD BE PUT IN A FOLDER, and as of 2026-10-26 it is the second.
 * The project tabs now create a record and file it in the same gesture, which is the door that had
 * to exist once a project became the only way to reach its ingredients. This one stays, and is now
 * the answer to a different question: filing a record that ALREADY EXISTS, or moving one between
 * folders -- both decisions you make while looking at the record rather than at a folder.
 *
 * SO THE TWO ARE NOT ALTERNATIVES. Creating into a folder cannot move anything, and this cannot
 * create anything; between them a record reaches a project whichever end you start from.
 *
 * THE MOVE IS NAMED BEFORE IT HAPPENS, which is the only surprising thing about this control. A
 * record belongs to exactly one project — enforced by a unique index, so "which project is this in"
 * has one answer by construction — and filing it into a second SILENTLY VACATES the first. That is
 * right: somebody who files a quote under the wrong folder and then the right one is doing an
 * ordinary thing, and refusing would make them find and undo the first filing. But silent is wrong
 * on a screen, so when the record is already somewhere the dialog says which folder it is leaving.
 *
 * ONLY FOR THE KINDS A PROJECT HOLDS. A project holds quote requests, prepared quotes, contracts,
 * build plans and products; a Document, an Item, a Supplier or an Equipment record is not something
 * it files. The caller decides by passing a memberType, and passing none renders nothing — so a
 * classification that is not a member can never grow a control that would refuse on use.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select,
        Typography} from "@mui/material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {hasPrivilege} from "../../util/SessionContext";
import {Blue3, Grey5, Grey7, Grey17} from "../../theme/styles";

/** Object classifications a project can hold, mapped to the member type the service names.
 *  A PRODUCT IS NOT HERE because it is not a classification (2026-10-29): it is an Item of type
 *  PRT-, and `fileableKind` in util/projectMembers.ts answers for it from the type. */
export const FILEABLE: Record<string, string> = {
    PreparedQuote: "PreparedQuote",
    Contract: "Contract",
    BuildPlan: "BuildPlan",
};

type Project = {USID: string; Name?: string | null; Status?: string | null};

//: THE STATUSES A FOLDER STILL TAKES RECORDS IN, which is not the same question as whether its
//: contents may be CHANGED -- a RELEASED project accepts filings and freezes what is in it. Mirrors
//: project.FILEABLE_STATUSES and ProjectMembers' own copy.
const FILEABLE_STATUSES = ["DRAFT", "RELEASED"];

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

export default function ProjectFiling(
    {objectUSID, memberType}: {objectUSID?: string | null; memberType?: string | null}) {

    const appContext: any = useContext(AppContext);
    const api = useMemo(() => makeCresAxios(appContext?.sessionContext),
                        // eslint-disable-next-line react-hooks/exhaustive-deps
                        [appContext?.sessionContext]);
    const navigate = useNavigate();

    const [held, setHeld] = useState<Project | null>(null);
    const [known, setKnown] = useState(false);
    const [choosing, setChoosing] = useState(false);
    const [projects, setProjects] = useState<Project[]>([]);
    const [chosen, setChosen] = useState<string>("");
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!objectUSID || !memberType) return;
        api.get("t1/v1/project_members", {
            params: {MemberType: memberType, MemberUSID: objectUSID},
            headers: {"idoc-response": "v1.ProjectRevision-001"},
        })
            .then((answer: any) => {
                setHeld(listOf<Project>(
                    answer?.data?.PlatformMessage?.ListOfProjectRevision?.ProjectRevision)[0]
                    ?? null);
                setKnown(true);
            })
            // A READ THAT WILL NOT ANSWER LEAVES THE LINE OFF THE PAGE rather than showing "not in
            // a project", which would be a claim this component cannot stand behind. `known` stays
            // false and nothing renders.
            .catch(() => { setHeld(null); setKnown(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, objectUSID, memberType]);

    useEffect(() => { load(); }, [load]);

    const openChooser = useCallback(() => {
        setRefusal(null);
        setChosen("");
        setChoosing(true);
        api.get("t1/v1/projects", {headers: {"idoc-response": "v1.ProjectRevision-001"}})
            // ONLY A PROJECT THAT STILL TAKES RECORDS. A deprecated or cancelled folder is where
            // finished work went, and offering one would be offering to put live work into last
            // quarter's folder.
            //
            // FILTERED HERE AND NOT IN THE REQUEST, because `Status` takes one value and two are
            // fileable. Asking for `Status: "OPEN"` is what this did until 2026-10-27 -- a status a
            // project has not had since it became a species, so the request matched nothing, the
            // chooser was empty, and `.catch` turned the whole failure into a list of none. Nothing
            // could be filed into a project at all and the screen said only "no projects".
            .then((answer: any) => setProjects(
                listOf<Project>(
                    answer?.data?.PlatformMessage?.ListOfProjectRevision?.ProjectRevision)
                    .filter(one => FILEABLE_STATUSES.includes(
                        String(one.Status ?? "").toUpperCase()))))
            .catch(() => setProjects([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    const file = useCallback(() => {
        setBusy(true);
        setRefusal(null);
        api.post("t1/v1/project_members",
                 {USID: chosen, MemberType: memberType, MemberUSID: objectUSID})
            .then(() => { setChoosing(false); load(); })
            .catch((problem: any) => setRefusal(
                problem?.response?.data?.Cause
                ?? "Filing it did not work, and the reason did not come back."))
            .finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, chosen, memberType, objectUSID, load]);

    const unfile = useCallback(() => {
        setBusy(true);
        setRefusal(null);
        api.delete("t1/v1/project_members",
                   {params: {MemberType: memberType, MemberUSID: objectUSID}})
            .then(() => load())
            .catch((problem: any) => setRefusal(
                problem?.response?.data?.Cause
                ?? "Removing it did not work, and the reason did not come back."))
            .finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, memberType, objectUSID, load]);

    if (!objectUSID || !memberType || !known) return null;

    // THE WRITE PRIVILEGE THE SERVICE ITSELF USES, so the control is absent rather than present
    // and refusing. The service is still the boundary -- this only stops the page offering
    // somebody a button that cannot work.
    //
    // `project:create`, WHICH IS WHAT add_to_project ACTUALLY ASKS FOR. This said
    // `preparedQuote:create` until 2026-10-27, which was true when filing was reached from the
    // quote and stopped being true when the ingredients' own privileges were retired -- the name no
    // longer exists, so `hasPrivilege` answered false for everybody and the control vanished for
    // everybody. Nothing failed: a hidden button and an absent feature look identical.
    const mayFile = hasPrivilege("project:create");

    // THE DETAIL PANE'S OWN LABEL STYLE, copied rather than invented: a quiet grey label over a
    // dark value, at the same size and colour as Approval, Released and Effective beside it. The
    // uppercase, letter-spaced label this started with is the Projects page's idiom and competes
    // with the value it introduces -- which is exactly the note already written in `Field` above
    // this component's insertion point, and which I did not read before writing it.
    return <Box sx={{display: "flex", flexDirection: "column", gap: "1px", minWidth: 220}}>
        <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.4}}>Project</Typography>
        <Box sx={{display: "flex", alignItems: "center", gap: "10px", minHeight: "21px",
                  flexWrap: "wrap"}}>
            {held
                ? <Typography sx={{fontSize: 13, color: Blue3, cursor: "pointer"}}
                              onClick={() => navigate(`/projects/${held.USID}`)}>
                      {held.Name || "—"}
                  </Typography>
                : <Typography sx={{fontSize: 13, color: Grey17}}>Not in a project</Typography>}
            {mayFile && (
                <Typography sx={{fontSize: 12, color: Blue3, cursor: "pointer"}}
                            onClick={openChooser}>
                    {held ? "Move" : "File"}
                </Typography>
            )}
            {mayFile && held && (
                <Typography sx={{fontSize: 12, color: Grey7, cursor: "pointer"}}
                            onClick={unfile}>
                    Remove
                </Typography>
            )}
        </Box>
        {refusal && (
            <Typography sx={{fontSize: 11.5, color: "#B3261E", lineHeight: 1.5, paddingTop: "4px",
                             maxWidth: 320}}>
                {refusal}
            </Typography>
        )}

        <Dialog open={choosing} onClose={() => setChoosing(false)} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontSize: 16}}>
                {held ? "Move this record to another project" : "File this record in a project"}
            </DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: "16px",
                                paddingTop: "8px !important"}}>
                {/* THE MOVE, NAMED. A record is in exactly one project, so this takes it out of
                    the one it is in -- silently, in the database, which is right there and wrong
                    on a screen. */}
                {held && (
                    <Typography sx={{fontSize: 12.5, color: "#8A6100", lineHeight: 1.6}}>
                        This record is filed in <strong>{held.Name}</strong>. A record belongs to
                        exactly one project, so filing it elsewhere takes it out of that one.
                    </Typography>
                )}
                {projects.length === 0 && (
                    <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.6}}>
                        There are no open projects to file this in. Open one from the Projects
                        page first.
                    </Typography>
                )}
                {projects.length > 0 && (
                    <Select size="small" displayEmpty value={chosen}
                            onChange={event => setChosen(String(event.target.value))}>
                        <MenuItem value="" disabled>Choose a project</MenuItem>
                        {projects.filter(one => one.USID !== held?.USID).map(one => (
                            <MenuItem key={one.USID} value={one.USID}>{one.Name}</MenuItem>
                        ))}
                    </Select>
                )}
                {refusal && (
                    <Typography sx={{fontSize: 12.5, color: "#B3261E", lineHeight: 1.6}}>
                        {refusal}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={() => setChoosing(false)}
                        sx={{textTransform: "none"}}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation
                        disabled={!chosen || busy} onClick={file}
                        sx={{textTransform: "none"}}>
                    {busy ? "Filing…" : (held ? "Move it" : "File it")}
                </Button>
            </DialogActions>
        </Dialog>
    </Box>;
}
